import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MainMapService } from '../../../../../shared/service/main-map.service';
import { WidgetNotifyService } from '../../../../../shared/service/widget-notify.service';
import { EsriExpand } from '../../../../../config/esri.module';
import { GoogleMapsService, GoogleMapsWidgetMode, GoogleMapsWidgetOptions } from '@sbb-amsgis/google-maps';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { GoogleMapsStatus } from '@sbb-amsgis/google-maps/lib/types/google-maps-status';
import { CONSTANTS_VALUE } from '../../../../../config/constants.module';
import { BaseWidget } from '../../../base-widget/base-widget';

@Component({
    selector: 'google-maps-tool',
    templateUrl: './google-maps-tool.component.html',
    styleUrl: './google-maps-tool.component.scss',
    standalone: false
})
export class GoogleMapsToolComponent extends BaseWidget implements OnInit, AfterViewInit {
  @ViewChild('googleMapComponentRef', { static: true }) googleMapComponentRef: ElementRef;

  widgetOptions: GoogleMapsWidgetOptions;
  mode: GoogleMapsWidgetMode = 'streetViewCentered';
  status: GoogleMapsStatus = 'inactive';

  private _subscriptions: Subscription[] = [];

  constructor(
    public mainMapService: MainMapService,
    public readonly googleMapsWidgetService: GoogleMapsService,

    public widgetNotifyService: WidgetNotifyService,
    private translate: TranslateService,
    private readonly cd: ChangeDetectorRef
  ) {
    super(mainMapService, widgetNotifyService);
    // set widget initial options
    this.widgetOptions = {
      extent: this.mainMapService.mapView.extent,
      mode: 'streetViewCentered',
      features: [],
      showDirections: false,
      mapView: this.mainMapService.mapView,
      queryFeatures: true,
      zoomLevel: 18,
    };
  }

  ngOnInit() {
    // get MainMapService
    const mapView: any = this.mainMapService.mapView;
    this._subscriptions.push(
      this.googleMapsWidgetService.status$.subscribe(status => {
        this.status = status;
        this.cd.detectChanges();
      })
    );

    this._subscriptions.push(
      this.googleMapsWidgetService.widgetMode$.subscribe(mode => {
        this.mode = mode;
        this.cd.detectChanges();
      })
    );

    // Create an Expand instance and set the content
    const expandProperties: __esri.ExpandProperties = {
      view: mapView,
      id: 'GoogleMapsComponent',
      content: this.googleMapComponentRef.nativeElement,
      expandIcon: 'person',
      expanded: true,
    };
    this.expandWidget = new EsriExpand(expandProperties);
    // translate tooltip icon
    super.translateToolTipWidgetIcon(this.expandWidget, this.translate, 'WIDGET.GOOGLE_MAPS.TOOLTIP');

    // add widget to the main map
    this.addWidgetToMainMap(mapView);
  }

  ngAfterViewInit() {
    // super.initDraggable(this.googleMapComponentRef);
    // listen on language change
    this.translate.onLangChange.subscribe(() => {
      super.translateToolTipWidgetIcon(this.expandWidget, this.translate, 'WIDGET.PRINT.TOOLTIP');
    });
  }

  /**
   * @function setInitValues
   * @param id
   * @param expand
   * @description Sets the initial values
   */
  public setInitValues(id: string, expand: boolean) {
    super.setInitValues(id, expand);
    this.mainMapService.deactivateMapInteraction();
  }

  /**
   * @function destroySelf
   * @description First notify with componentDestroySubject that this component
   * will be destroyed. Destroy self and the widget reference from DOM and also from memory
   */
  public destroySelf() {
    super.destroySelf();
    this.mainMapService.activateMapInteraction();
  }

  /**
   * @function destroySelf
   * @description Destroy the Esri Expand Widget of this component
   */
  public destroyWidget() {
    super.destroyWidget();
    this._subscriptions.forEach(x => x.unsubscribe());
  }

  public setMode(mode: GoogleMapsWidgetMode) {
    switch (mode) {
      case 'sideBySide':
        this.googleMapsWidgetService.setWidgetMode('sideBySide');
        break;
      case 'streetViewCentered':
        this.googleMapsWidgetService.setWidgetMode('streetViewCentered');
        break;
    }
  }

  public selectPanoramaLocation() {
    this.mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.crosshair);
    this.mainMapService.deactivateMapInteraction();
    this.googleMapsWidgetService
      .selectPanoramaLocation()
      .pipe(
        finalize(() => {
          this.mainMapService.setCursor(CONSTANTS_VALUE.CURSOR.default);
          this.mainMapService.activateMapInteraction();
        })
      )
      .subscribe(() => {});
  }

  public centerMap() {
    this.googleMapsWidgetService.centerEsriMap().subscribe();
  }
  /* private methodes */

  /**
   * adds the new widget to a given map
   * @param mapView the map to add the widget to
   */
  private addWidgetToMainMap(mapView: any) {
    mapView.ui.add(this.expandWidget, 'top-right');
    this.mainMapService.registerWidget(this.expandWidget);
  }
}
